import React from "react";
import SettingsSectionIntro from "./SettingsSectionIntro";
import SettingsCard from "./SettingsCard";
import EmailConfigField, { INPUT_BASE_CLASS } from "./EmailConfigField";
import DropdownField from "../FileManager/DropdownField";

export default function EmailServiceSettingsPage({
  user,
  emailConfig,
  loadingEmailConfig,
  savingEmailConfig,
  emailConfigError,
  emailConfigSuccess,
  onEmailConfigChange,
  onEmailCredentialChange,
  onSaveEmailServiceConfig,
}) {
  if (!user?.is_prime_superuser) {
    return (
      <div className="w-full rounded-2xl border border-red-100 bg-white p-6 shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
        <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3">
          <p className="text-sm font-inter text-red-600">
            Only prime superusers can manage email service configuration.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 w-full">
      <SettingsSectionIntro
        title="Email Service"
        description="Configure sender identity, provider selection, and delivery credentials."
      />
      <SettingsCard
        title="Provider Configuration"
        badge={
          <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-inter font-medium border ${
            emailConfig.is_active
              ? "bg-green-50 text-green-700 border-green-100"
              : "bg-gray-50 text-gray-600 border-gray-200"
          }`}>
            {emailConfig.is_active ? "Active" : "Inactive"}
          </span>
        }
      >
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <EmailConfigField label="Provider">
            <DropdownField
              name="provider"
              value={emailConfig.provider}
              onChange={(e) => onEmailConfigChange("provider", e.target.value)}
              options={[
                { value: "sendgrid", label: "SendGrid" },
                { value: "smtp", label: "SMTP" },
              ]}
              placeholder="Select provider"
              className="h-11 border-gray-200 bg-white bg-none px-4 pr-12 text-sm font-inter shadow-none hover:border-primary-blue hover:shadow-none"
            />
          </EmailConfigField>

          <EmailConfigField
            label="Sender Name"
            value={emailConfig.sender_name}
            onChange={(e) => onEmailConfigChange("sender_name", e.target.value)}
            placeholder="Samuel"
          />

          <EmailConfigField
            label="Sender Local Part"
            value={emailConfig.sender_local_part}
            onChange={(e) => onEmailConfigChange("sender_local_part", e.target.value)}
            placeholder="samuel"
          />

          <EmailConfigField
            label="Sender Domain"
            value={emailConfig.sender_domain}
            onChange={(e) => onEmailConfigChange("sender_domain", e.target.value)}
            placeholder="business.com"
          />

          {emailConfig.provider === "sendgrid" ? (
            <EmailConfigField
              label="SendGrid API Key"
              type="password"
              value={emailConfig.api_key}
              onChange={(e) => onEmailConfigChange("api_key", e.target.value)}
              placeholder="SG...."
              className="md:col-span-2"
            />
          ) : (
            <>
              <EmailConfigField
                label="SMTP Host"
                value={emailConfig.credentials.host}
                onChange={(e) => onEmailCredentialChange("host", e.target.value)}
                placeholder="smtp.business.com"
              />
              <EmailConfigField
                label="SMTP Port"
                type="number"
                value={emailConfig.credentials.port}
                onChange={(e) => onEmailCredentialChange("port", Number(e.target.value))}
              />
              <EmailConfigField
                label="SMTP Username"
                value={emailConfig.credentials.username}
                onChange={(e) => onEmailCredentialChange("username", e.target.value)}
              />
              <EmailConfigField
                label="SMTP Password"
                type="password"
                value={emailConfig.credentials.password}
                onChange={(e) => onEmailCredentialChange("password", e.target.value)}
              />
            </>
          )}

          <label className="flex items-center gap-3 text-sm md:col-span-2 rounded-xl border border-gray-100 bg-gray-50/70 px-4 py-3">
            <input
              type="checkbox"
              checked={emailConfig.is_active}
              onChange={(e) => onEmailConfigChange("is_active", e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-blue focus:ring-primary-blue"
            />
            <span className="font-inter text-sm text-gray-700">Configuration is active</span>
          </label>
        </div>

        <div className="px-6 pb-6">
          {emailConfigError && <p className="text-sm font-inter text-red-600 mb-2">{emailConfigError}</p>}
          {emailConfigSuccess && <p className="text-sm font-inter text-green-600 mb-2">{emailConfigSuccess}</p>}
          <button
            onClick={onSaveEmailServiceConfig}
            disabled={loadingEmailConfig || savingEmailConfig}
            className="px-5 py-2.5 bg-gradient-to-r from-primary-blue to-blue-400 text-white text-sm font-inter font-semibold rounded-lg hover:from-blue-600 hover:to-blue-500 disabled:opacity-50 transition-colors shadow-sm"
          >
            {savingEmailConfig ? "Saving..." : "Save Email Configuration"}
          </button>
        </div>
      </SettingsCard>
    </div>
  );
}
